import { useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BarChart3, Lightbulb, Folder, Activity, Users, Shield, Settings, LogOut, Bug, HelpCircle, SquareKanban } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth-store";
import { useChatStore } from "@/stores/chat-store";
import { getInitials } from "@/lib/get-initials";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useMe } from "@/hooks/queries/use-me";

/* ── Tab definitions ─────────────────────────────────── */

interface TabItem {
  key: string;
  label: string;
  icon: typeof BarChart3;
  href: string;
  /** Path prefixes to match for active state */
  activePrefixes: string[];
}

const tabs: TabItem[] = [
  {
    key: "home",
    label: "Home",
    icon: BarChart3,
    href: "/dashboard",
    activePrefixes: ["/dashboard"],
  },
  {
    key: "requests",
    label: "Requests",
    icon: Lightbulb,
    href: "/epic-requests",
    activePrefixes: ["/epic-requests"],
  },
  {
    key: "epics",
    label: "Epics",
    icon: Folder,
    href: "/epics",
    activePrefixes: ["/epics", "/features", "/tasks"],
  },
  {
    key: "activity",
    label: "Activity",
    icon: Activity,
    href: "/activity",
    activePrefixes: ["/activity"],
  },
];

/* ── Component ───────────────────────────────────────── */

/**
 * Mobile bottom dock — mirrors the desktop BottomDock style.
 *
 * - Fixed to the bottom of the viewport with glass-card background
 * - 4 nav tabs + user avatar: Home, Requests, Epics, Activity, [Avatar]
 * - Matches desktop dock visual language (pill items, glass-card, separator)
 * - Uses safe-area-inset-bottom for notched devices
 * - All touch targets are 44px minimum
 */
export function MobileTabBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { data: meData } = useMe();
  const hasFogbugzKey = meData?.hasFogbugzKey ?? false;
  const hasAdoPat = meData?.hasAdoPat ?? false;
  const [menuOpen, setMenuOpen] = useState(false);
  const openHelpCenter = useChatStore((s) => s.openHelpCenter);

  const isTabActive = (tab: TabItem): boolean => {
    return tab.activePrefixes.some(
      (prefix) =>
        location.pathname === prefix ||
        location.pathname.startsWith(prefix + "/"),
    );
  };

  const isSettingsActive = location.pathname.startsWith("/settings");
  const isTeamsActive = location.pathname.startsWith("/teams");
  const isAdminActive = location.pathname.startsWith("/admin");
  const isFogbugzActive = location.pathname.startsWith("/fogbugz");
  const isAdoActive = location.pathname === "/ado" || location.pathname.startsWith("/ado/");

  const menuItems = [
    { label: "Teams", icon: Users, href: "/teams", active: isTeamsActive },
    ...(hasFogbugzKey
      ? [{ label: "FogBugz", icon: Bug, href: "/fogbugz/tickets", active: isFogbugzActive }]
      : []),
    ...(hasAdoPat
      ? [{ label: "ADO", icon: SquareKanban, href: "/ado/items", active: isAdoActive }]
      : []),
    ...(user?.isGlobalAdmin
      ? [
          { label: "Admin", icon: Shield, href: "/admin/users", active: location.pathname === "/admin/users" },
          { label: "Activity", icon: Activity, href: "/admin/activity", active: location.pathname.startsWith("/admin/activity") },
        ]
      : []),
    { label: "Settings", icon: Settings, href: "/settings", active: isSettingsActive },
  ];

  const handleMenuNav = (href: string) => {
    setMenuOpen(false);
    void navigate(href);
  };

  const handleLogout = () => {
    setMenuOpen(false);
    logout();
    void navigate("/login");
  };

  return (
    <nav
      aria-label="Main navigation"
      className="relative"
    >
      <div className="flex items-center gap-1 px-2 py-2">
        {tabs.map((tab) => {
          const active = isTabActive(tab);
          const Icon = tab.icon;

          return (
            <button
              key={tab.key}
              aria-current={active ? "page" : undefined}
              aria-label={tab.label}
              onClick={() => { void navigate(tab.href); }}
              className={cn(
                "relative flex flex-1 flex-col items-center justify-center gap-0.5 h-12 rounded-xl transition-all touch-target",
                active
                  ? "bg-white/[0.1] text-foreground"
                  : "text-muted-foreground active:text-foreground active:bg-white/[0.06]",
              )}
            >
              {active && (
                <motion.div
                  layoutId="mobile-tab-indicator"
                  className="absolute inset-0 rounded-xl bg-white/[0.1]"
                  transition={{ type: "spring", stiffness: 500, damping: 35 }}
                />
              )}
              <Icon className="relative z-10 h-5 w-5 shrink-0" />
              <span className="relative z-10 text-[11px] font-medium leading-none">
                {tab.label}
              </span>
            </button>
          );
        })}

        {/* Help button */}
        <button
          aria-label="Help Center"
          onClick={() => { openHelpCenter("menu"); }}
          className={cn(
            "relative flex flex-col items-center justify-center gap-0.5 h-12 w-12 rounded-xl transition-all shrink-0",
            "text-muted-foreground active:text-foreground active:bg-white/[0.06]"
          )}
        >
          <HelpCircle className="h-5 w-5 shrink-0" />
          <span className="text-[11px] font-medium leading-none">Help</span>
        </button>

        {/* Separator */}
        <div className="w-px h-5 bg-white/[0.08] mx-0.5 shrink-0" />

        {/* User avatar — opens popup menu */}
        <div className="relative shrink-0">
          <button
            onClick={() => { setMenuOpen(!menuOpen); }}
            aria-label="More options"
            className={cn(
              "flex items-center justify-center h-11 w-11 rounded-xl transition-all",
              (isSettingsActive || isTeamsActive || isAdminActive || menuOpen)
                ? "ring-2 ring-primary/60 ring-offset-1 ring-offset-background"
                : "active:bg-white/[0.06]",
            )}
          >
            <Avatar className="h-7 w-7">
              {user?.avatarUrl && <AvatarImage src={user.avatarUrl} alt={user?.name ?? ""} />}
              <AvatarFallback className="text-[10px] bg-muted">
                {getInitials(user?.name ?? "")}
              </AvatarFallback>
            </Avatar>
          </button>

          {/* Popup menu — rendered outside the relative container */}
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => { setMenuOpen(false); }} />
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              transition={{ duration: 0.15, ease: "easeOut" }}
              className="absolute left-0 right-0 bottom-full mb-2 z-50 rounded-xl border border-border bg-popover p-1 text-popover-foreground shadow-xl overflow-hidden"
            >
              {/* User info */}
              <div className="px-3 py-2.5 border-b border-border mb-1">
                <p className="text-sm font-medium text-foreground truncate">{user?.name}</p>
                <p className="text-[11px] text-muted-foreground truncate">{user?.email}</p>
              </div>

              {/* Menu items */}
              {menuItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.label}
                    onClick={() => { handleMenuNav(item.href); }}
                    className={cn(
                      "flex items-center gap-3 w-full px-3 py-3 text-sm rounded-lg transition-colors",
                      item.active
                        ? "text-foreground bg-muted/50"
                        : "text-popover-foreground hover:bg-muted/50 active:bg-muted/50",
                    )}
                  >
                    <Icon className="h-4 w-4" />
                    {item.label}
                  </button>
                );
              })}

              {/* Sign out */}
              <div className="border-t border-border mt-1 pt-1">
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3 w-full px-3 py-3 text-sm text-destructive rounded-lg active:bg-muted/50 transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                  Sign Out
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </nav>
  );
}
